/**
 * F 327 — THE CREDENTIAL BINDING.
 *
 * The dispatch requires a credential to be "a node or a Setting, and visible
 * in the settings page F 324 built". FCr turns out to already have a
 * `credential` ENTITY KIND and a `Credential` model carrying
 * `encryptedValue`/`iv`/`salt`/`algorithm`/`kdf` — imported by a barrel, an
 * index and a test, and by nothing else. It is a wiring-dead model.
 *
 * Gemini's verdict on the fork, asked with every fact inlined:
 * **BINDING_2** — "the system already provides a complete Credential entity
 * model and an isolated CredentialStore designed specifically to hold
 * ciphertext, keeping it distinct from user settings… Building Binding 1: No.
 * Building Binding 2: Yes." Its Q4 added the condition that decided it: a
 * flat settings stub is the wrong answer once a credential needs LIFECYCLE
 * (rotation, revocation, scoping).
 *
 * So this module does two things and neither duplicates anything:
 *
 *   1. `credentialNodeFrom` builds the CANONICAL `credential` node — the kind
 *      that already exists, carrying the sealed payload the store produced.
 *      This is the credential's home, and it is where rotation happens.
 *
 *   2. `credentialSettingFrom` builds the SETTINGS-PAGE node — and it holds a
 *      POINTER (`ref`) to that canonical node plus its display fields. NOT a
 *      copy. Nothing secret is duplicated, so the settings blob — which is
 *      synced to /api/fcc/kv as one JSON document — never carries key
 *      material, while the page still shows "Groq · pipeline key · present".
 *
 * WHY A POINTER AND NOT A COPY
 * ----------------------------
 * A copy would put a second, synced copy of the sealed secret into the
 * settings blob for no benefit: the page never displays the ciphertext, and
 * rotation would have two homes to update, one of which nothing reads.
 *
 * NO NEW EntityKind: 'credential' is already in the canonical union.
 */

import type { Setting } from '../../types/models/Setting';
import { credentialFcid, type Credential, type CredentialEntityKind } from '../../types/models/Credential';
import type {
  CredentialMeta,
  CredentialProvider,
  Principal,
  StoredCredential,
} from './credentialStore';

/** Group prefix. The settings page groups by "the key minus its last segment". */
export const CREDENTIAL_KEY_PREFIX = 'credential.';

/** Key for a credential's visible Setting node. */
export function credentialSettingKey(provider: CredentialProvider, name: string): string {
  return `${CREDENTIAL_KEY_PREFIX}${provider}.${name}`;
}

/** Is this key one of ours? */
export function isCredentialKey(key: string): boolean {
  return key.startsWith(CREDENTIAL_KEY_PREFIX);
}

/**
 * What a credential SETTINGS node's value holds: a POINTER plus display
 * fields, and nothing else.
 *
 * Declared field by field rather than as `unknown`, because the point of the
 * type is that there is NOWHERE for a key to go. Adding a field to this
 * interface is the only way to make one appear, and that is a reviewable edit.
 */
export interface CredentialSettingValue {
  /** The canonical credential node this row points at. A reference, not a copy. */
  ref: string;
  /** Which provider. The dispatch asks for this. */
  provider: CredentialProvider;
  /** Human label. The dispatch asks for this. */
  label: string;
  /** What kind of secret it is. */
  kind: string;
  /** The credential's name within its provider. */
  name: string;
  /** Always true. "It exists", not "what it is". Mirrors CredentialMeta. */
  present: true;
  /** When the ciphertext behind this was last sealed. */
  updatedAt: number;
  /**
   * WHERE THE KEY LIVES, stated so a reader of the graph knows the settings
   * blob is not it. A path is not a secret.
   */
  sealedIn: 'credential-node';
}

/**
 * F 390 — THE PRINCIPAL ID OUT OF A CREDENTIAL ID.
 *
 * `qualify()` in credentialStore.ts builds the id as
 * `${scope}:${principalId}:${provider}:${name}`. The scope and the provider
 * and the name are all controlled tokens; the principal id is the one field
 * with a shape nobody chose, so it is taken as "everything between the first
 * and the second colon".
 *
 * A record whose id does not have that shape yields the scope, which is
 * what the node used to carry — so a malformed id degrades to the old,
 * wrong-but-safe behaviour rather than to a wrong principal.
 */
export function principalIdFromCredentialId(id: string): string {
  const parts = id.split(':');
  if (parts.length < 3) return id;
  return parts.slice(1, -2).join(':') || parts[1];
}

/**
 * The canonical credential NODE — the credential's real home.
 *
 * Built from a `StoredCredential` (the store's own record, which already holds
 * `ciphertext`/`iv`/`salt`/`algorithm`/`kdf`), NOT from `CredentialMeta`, so
 * the sealed payload travels to its own node and not into the settings blob.
 */
export function credentialNodeFrom(rec: StoredCredential): Credential {
  return {
    id: rec.id,
    fcid: credentialFcid(rec.id),
    name: rec.name,
    label: rec.label,
    provider: rec.provider,
    kind: rec.kind as CredentialEntityKind,
    // F 390 — THE PRINCIPAL ID, NOT THE SCOPE.
    //
    // This read `principal: { scope: rec.scope, id: rec.scope }` — the SCOPE
    // written into the id field, so the node claimed a principal id of "user"
    // or "operator" rather than of a person. The `Credential` model has a
    // `principal: Principal` field and it was lying.
    //
    // It matters because the principal id is the SALT INPUT of the passphrase
    // key: `derivePassphraseKey(passphrase, principalId)` derives
    // PBKDF2(passphrase, base64('fcr.passkey.v1.' + principalId)). A reader
    // that wants to OPEN a sealed credential — the Lane Bridge, reading the
    // graph — must know the principal id, and the record's id is where it
    // lives.
    principal: { scope: rec.scope, id: principalIdFromCredentialId(rec.id) },
    scope: rec.scope,
    encryptedValue: rec.ciphertext,
    iv: rec.iv,
    salt: rec.salt,
    algorithm: rec.algorithm,
    kdf: rec.kdf,
    description: rec.description,
    createdAt: new Date(rec.createdAt).toISOString(),
    updatedAt: new Date(rec.updatedAt).toISOString(),
  };
}

/**
 * The fields that must NEVER appear in a credential Setting's value.
 *
 * Exported so the tests can assert against the list rather than against a
 * hand-written expectation that drifts.
 */
export const FORBIDDEN_IN_GRAPH = [
  'value',
  'token',
  'key',
  'secret',
  'plaintext',
  'passphrase',
  'ciphertext',
  'iv',
  'salt',
] as const;

/**
 * Build the visible Setting node for one credential.
 *
 * `editable: false` is load-bearing twice over: the page must not offer an
 * editor (Q1's paste-a-plaintext-key risk), and the generic renderer must not
 * be the thing a user types into.
 */
export function credentialSettingFrom(meta: CredentialMeta): Setting {
  const value: CredentialSettingValue = {
    ref: `fcid://credential/${meta.id}`,
    provider: meta.provider,
    label: meta.label,
    kind: meta.kind,
    name: meta.name,
    present: true,
    updatedAt: meta.updatedAt,
    sealedIn: 'credential-node',
  };
  return {
    id: `global::${credentialSettingKey(meta.provider, meta.name)}`,
    fcid: `fcid://setting/global::${credentialSettingKey(meta.provider, meta.name)}`,
    key: credentialSettingKey(meta.provider, meta.name),
    label: meta.label,
    value,
    valueType: 'json',
    scope: 'global',
    scopeId: null,
    // Never a free-text field: there is nothing here a user should type.
    editable: false,
    description:
      `Secret for ${meta.provider}. Sealed in its own credential node; ` +
      `this row is a pointer, not a copy. Not in the settings document.`,
  };
}

/**
 * EVERY field on a credential Setting, as paths, for the plaintext guard.
 *
 * Walks the value so a nested addition cannot smuggle a key past a check that
 * only looked at the top level.
 */
export function credentialValuePaths(value: unknown, prefix = ''): string[] {
  if (value === null || typeof value !== 'object') return [prefix || '(root)'];
  const out: string[] = [];
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    const p = prefix ? `${prefix}.${k}` : k;
    out.push(p);
    if (v && typeof v === 'object') out.push(...credentialValuePaths(v, p));
  }
  return out;
}

/**
 * Does this credential Setting carry anything it must not?
 *
 * Returns the offending paths, empty when clean. A test asserts this on every
 * field of every node; the guard is the thing being trusted, so it is written
 * to be obviously right rather than clever.
 */
export function findForbiddenFields(value: unknown): string[] {
  const paths = credentialValuePaths(value);
  return paths.filter((p) => {
    const leaf = p.split('.').pop() ?? p;
    return (FORBIDDEN_IN_GRAPH as readonly string[]).includes(leaf.toLowerCase());
  });
}

/**
 * The summary a UI can show without touching the store.
 *
 * Deliberately returns only booleans and the label — never a value — so this
 * function is safe to call from a render path and safe to log.
 */
export interface CredentialSummary {
  provider: CredentialProvider;
  label: string;
  name: string;
  present: true;
}

export function summarizeCredential(meta: CredentialMeta): CredentialSummary {
  return { provider: meta.provider, label: meta.label, name: meta.name, present: true };
}

/**
 * Write one credential's visible row into the settings graph.
 *
 * THE LOOP F 324 FOUND, CLOSED: F 324's settings surface existed, rendered
 * correctly, and was unreachable because nothing imported it. The same defect
 * is available here — a binding nothing calls is a binding that does not exist.
 * This is the caller.
 *
 * It writes the POINTER row only. The sealed payload goes to the credential
 * node, so this never moves key material into the settings document.
 *
 * Returns the keys it wrote, so a caller can log WHICH credentials are present
 * without ever touching a value.
 */
export function syncCredentialSetting(
  settings: SettingStoreLike,
  meta: CredentialMeta,
): string {
  const node = credentialSettingFrom(meta);
  settings.setSetting({
    key: node.key,
    value: node.value,
    valueType: node.valueType,
    scope: node.scope,
    scopeId: node.scopeId,
    operatorApproved: true,
  });
  return node.key;
}

/**
 * Sync every credential the store will list for a principal.
 *
 * Returns the keys written — names, never values, so the return value is safe
 * to log or put in a report.
 */
export async function syncCredentialSettings(
  settings: SettingStoreLike,
  credentials: { list: (p?: Principal) => Promise<CredentialMeta[]> },
  principal: Principal,
): Promise<string[]> {
  const metas = await credentials.list(principal);
  return metas.map((m) => syncCredentialSetting(settings, m));
}

/** The slice of `SettingStore` this module needs — keeps the import honest. */
export interface SettingStoreLike {
  setSetting: (input: {
    key: string;
    value: unknown;
    valueType: Setting['valueType'];
    scope: Setting['scope'];
    scopeId?: string | null;
    operatorApproved?: boolean;
  }) => { ok: boolean; code?: string };
}

/**
 * Read a credential Setting back into its typed value, or null if malformed.
 * Exported so the editor and the tests share one parser.
 */
export function asCredentialSettingValue(value: unknown): CredentialSettingValue | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Partial<CredentialSettingValue>;
  if (typeof v.provider !== 'string' || typeof v.label !== 'string') return null;
  if (v.present !== true || typeof v.ref !== 'string') return null;
  return {
    ref: v.ref,
    provider: v.provider,
    label: v.label,
    kind: typeof v.kind === 'string' ? v.kind : 'api_key',
    name: typeof v.name === 'string' ? v.name : '',
    present: true,
    updatedAt: typeof v.updatedAt === 'number' ? v.updatedAt : 0,
    sealedIn: 'credential-node',
  };
}

/**
 * F 390 — THE CREDENTIAL'S HOME IS THE GRAPH.
 *
 * The credential NODE is the canonical home (this module's own doc says so),
 * and until F 390 NOTHING ever wrote it: `syncCredentialSettings` writes only
 * the POINTER row into the settings blob, and the sealed payload stayed in
 * the browser's own credential storage. So the graph held a pointer to a
 * secret it could not see, and the secret was somewhere else entirely — which
 * is what forced F 388 to invent a server-side file store to put it in.
 *
 * Now the sealed payload goes to the graph as a `credential` node, and the
 * pointer row still points at it. One home, which is what rotation needs.
 *
 * THE GRAPH KEY. `freshcards:credential:<id>` — the `freshcards:` prefix is
 * what makes it SYNCABLE, and the `<id>` is the credential's canonical id.
 */
export const CREDENTIAL_GRAPH_PREFIX = 'freshcards:credential:';

export function credentialGraphKey(id: string): string {
  return `${CREDENTIAL_GRAPH_PREFIX}${id}`;
}

/**
 * Write one sealed credential into the graph.
 *
 * The value written is the canonical `Credential` node, so the graph holds
 * ciphertext, the iv, the salt, the algorithm and the kdf — and never the
 * secret. `RemoteState.remoteAdapter.save` JSON-stringifies what it is
 * given, so the graph's value IS the node's JSON, and `load` parses it back.
 */
export async function writeCredentialNode(
  rec: StoredCredential,
  write: (key: string, node: Credential) => Promise<void>,
): Promise<string> {
  const node = credentialNodeFrom(rec);
  const key = credentialGraphKey(rec.id);
  await write(key, node);
  return key;
}
